"use client";
/**
 * The share dialog (spec §UI; the approved mock mockups/file-sharing-2026-09-25.html).
 * One per app (ShareDialogHost). Add people by exact email (Enter or comma; a pasted
 * list adds each), remove with Undo, switch general access, copy the permanent link.
 * The server decides everything; this only says what it answered, in its own toast
 * (DialogToast), because the app's toast is unreachable from inside a modal dialog.
 */
import {
  useId,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
} from "react";
import {
  normalizeShareEmail,
  splitEmails,
  type GeneralAccess,
  type SharePerson,
} from "@bash/shared";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatSize } from "@/components/files-browser/helpers";
import { cn } from "@/lib/utils";
import { shareUrl } from "@/lib/viewer-origin";
import { fetchInviteLink } from "@/lib/sharing/api";
import {
  useAddPerson,
  useRemovePerson,
  useSetGeneralAccess,
  useShareState,
  useShareSuggestions,
} from "@/lib/sharing/useSharing";
import type { ShareTarget } from "@/stores/shareDialogStore";
import { DialogToast, useDialogToast } from "./DialogToast";
import { ChevronIcon, GlobeIcon, LinkIcon, LockIcon } from "./icons";
import { Avatar, FileTile } from "./people";

type Note =
  | { tone: "plain" | "warn"; text: string }
  | { tone: "no_account"; email: string; invite: string | null };

/** What one add came to: whether the field may clear, and what the field should say. */
type Outcome = { clear: boolean; note: Note | null };

const GENERAL: Record<
  GeneralAccess,
  {
    label: string;
    menu: string;
    consequence: string;
    now: string;
    copied: string;
  }
> = {
  restricted: {
    label: "Restricted",
    menu: "Only people with access can open it",
    consequence: "Only people with access can open it with the link.",
    now: "Only people with access can open it now.",
    copied: "Link copied. Only people with access can open it.",
  },
  link: {
    label: "Anyone with the link",
    menu: "Anyone on the internet with the link can view",
    consequence: "Anyone on the internet with the link can view.",
    now: "Anyone with the link can view it now.",
    copied: "Link copied. Anyone with it can view.",
  },
};

export function ShareDialog({
  target,
  onClose,
}: {
  target: ShareTarget;
  onClose: () => void;
}) {
  const field = useRef<HTMLInputElement>(null);
  const { say, toastProps } = useDialogToast(() => field.current?.focus());
  const listId = useId();
  const share = useShareState(target.id);
  const state = share.data;
  // Reads and writes share one cache key: the id the dialog was opened with. The server
  // resolves a creation's head on every call, so an older version's id writes the head.
  const addPerson = useAddPerson(target.id);
  const removePerson = useRemovePerson(target.id);
  const setGeneral = useSetGeneralAccess(target.id);
  const [draft, setDraft] = useState("");
  /** What the field holds right now, for an add that answers after the person typed on. */
  const latestDraft = useRef("");
  const setField = (text: string) => {
    latestDraft.current = text;
    setDraft(text);
  };
  const [note, setNote] = useState<Note | null>(null);
  const [active, setActive] = useState(0);
  /** Esc or leaving the field closes the suggestions; typing opens them again. */
  const [dismissed, setDismissed] = useState(false);
  /** One add at a time: a second Enter, paste or pick while one is in flight is ignored. */
  const busy = useRef(false);
  const suggestionsAll = useShareSuggestions(true);

  const query = draft.trim().toLowerCase();
  const suggestions = useMemo(() => {
    if (!query || !state) return [];
    const has = new Set(state.people.map((p) => p.email.toLowerCase()));
    return suggestionsAll
      .filter(
        (p) =>
          !has.has(p.email.toLowerCase()) &&
          (p.name.toLowerCase().startsWith(query) ||
            p.email.toLowerCase().startsWith(query))
      )
      .slice(0, 5);
  }, [query, state, suggestionsAll]);
  const shown = dismissed ? [] : suggestions;

  /** Add one address. Successes toast; everything else is a note. `restoring` is the Undo. */
  const add = async (raw: string, restoring = false): Promise<Outcome> => {
    const email = normalizeShareEmail(raw);
    if (!email) {
      return {
        clear: false,
        note: {
          tone: "warn",
          text: "That doesn't look like an email address.",
        },
      };
    }
    if (
      !restoring &&
      state?.people.some((p) => p.email.toLowerCase() === email)
    ) {
      return {
        clear: true,
        note: { tone: "plain", text: `${email} already has access.` },
      };
    }
    const r = await addPerson.mutateAsync(email);
    if (!r.ok) {
      return {
        clear: false,
        note: {
          tone: "warn",
          text:
            r.code === "rate_limited"
              ? "You've added a lot of new people this hour. Try again later."
              : "Couldn't add them just now. Try again.",
        },
      };
    }
    const out = r.value;
    if (out.status === "added" || out.status === "already") {
      say(
        restoring
          ? "Access restored."
          : out.status === "added"
            ? out.notified
              ? `${out.person.name} can now view it. We let them know.`
              : `${out.person.name} can now view it.`
            : `${out.person.email} already has access.`
      );
      return { clear: true, note: null };
    }
    if (out.status === "self") {
      return {
        clear: true,
        note: { tone: "plain", text: "That's you. You already own it." },
      };
    }
    return {
      clear: false,
      note: { tone: "no_account", email, invite: await fetchInviteLink() },
    };
  };

  const once = async (work: () => Promise<void>) => {
    if (busy.current) return;
    busy.current = true;
    try {
      await work();
    } finally {
      busy.current = false;
    }
  };

  const addOne = (raw: string) =>
    once(async () => {
      const submitted = latestDraft.current;
      const out = await add(raw);
      setNote(out.note);
      // Only what was submitted clears: text typed while the add was out stays.
      if (out.clear && latestDraft.current === submitted) {
        setField("");
        setActive(0);
      }
    });

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" && shown.length) {
      e.preventDefault();
      setActive((i) => (i + 1) % shown.length);
    } else if (e.key === "ArrowUp" && shown.length) {
      e.preventDefault();
      setActive((i) => (i - 1 + shown.length) % shown.length);
    } else if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const pick = shown[active];
      if (pick) void addOne(pick.email);
      else if (draft.trim()) void addOne(draft);
    }
  };

  /** A pasted list adds each in turn; what failed goes back in the field, with the first failure's note. */
  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    // While an add is in flight the paste lands in the field as typed text.
    if (busy.current) return;
    const emails = splitEmails(e.clipboardData.getData("text"));
    if (emails.length < 2) return;
    e.preventDefault();
    void once(async () => {
      const failed: string[] = [];
      let failure: Note | null = null;
      let aside: Note | null = null;
      for (const email of emails) {
        const out = await add(email);
        if (out.clear) aside ??= out.note;
        else {
          failed.push(email);
          failure ??= out.note;
        }
      }
      setField(failed.join(", "));
      setNote(failure ?? aside);
    });
  };

  const remove = async (person: SharePerson) => {
    const r = await removePerson.mutateAsync(person);
    if (!r.ok) {
      say("Couldn't remove them just now. Try again.");
      return;
    }
    say(
      `${person.name} can no longer open it.`,
      () => void add(person.email, true).then((out) => setNote(out.note))
    );
  };

  const changeGeneral = async (general: GeneralAccess) => {
    if (!state || general === state.general) return;
    const r = await setGeneral.mutateAsync(general);
    say(
      r.ok ? GENERAL[general].now : "Couldn't change general access. Try again."
    );
  };

  /** The clipboard can refuse (no permission, an insecure page); say so rather than claim a copy. */
  const copy = async (text: string, done: string) => {
    try {
      await navigator.clipboard.writeText(text);
      say(done);
    } catch {
      say("Couldn't copy the link. Try again.");
    }
  };

  const sub = [
    `In ${target.where}`,
    target.sizeBytes ? formatSize(target.sizeBytes) : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const general = state?.general ?? "restricted";
  const g = GENERAL[general];
  // What is shared now: a creation opened from an older version is named as its head.
  const name = state?.name ?? target.name;
  const mimeType = state?.mimeType ?? target.mimeType;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      {/* The content itself does not scroll: its inner column does, so the toast can hang outside it. */}
      <DialogContent
        hideCloseButton
        // Esc closes an open suggestions list first, then the dialog.
        onEscapeKeyDown={(e) => {
          if (shown.length) {
            e.preventDefault();
            setDismissed(true);
          }
        }}
        // A shorter cap than the default, so the toast always has room outside the box.
        className="max-h-[calc(100dvh-5rem)] overflow-visible p-0 xs:p-0 sm:max-h-[calc(100dvh-8rem)] sm:max-w-130 sm:rounded-[22px]"
      >
        <div className="max-h-[inherit] overflow-y-auto overscroll-contain rounded-[inherit] px-4 py-5 xs:p-5.5 xs:pb-4.5">
          <div className="flex items-start gap-3">
            <FileTile name={name} mimeType={mimeType} />
            <div className="min-w-0 flex-1 pt-px">
              <DialogTitle className="truncate text-4.5 font-semibold leading-snug">{`Share "${name}"`}</DialogTitle>
              <DialogDescription className="mt-0.5 text-3 text-fg-muted">
                {sub}
              </DialogDescription>
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="size-8 shrink-0 rounded-full text-xl text-fg-muted hover:bg-bg-muted hover:text-fg-default"
            >
              ×
            </button>
          </div>

          <div className="relative mt-4.5">
            <div className="flex min-h-11.5 flex-wrap items-center gap-1.5 rounded-16 bg-bg-muted py-1.5 pl-3.5 pr-2 focus-within:shadow-[0_0_0_2px_color-mix(in_srgb,var(--kind-blue)_45%,transparent)]">
              <input
                ref={field}
                role="combobox"
                aria-label="Add people by email"
                aria-autocomplete="list"
                aria-expanded={shown.length > 0}
                aria-controls={listId}
                aria-activedescendant={
                  shown.length ? `${listId}-${active}` : undefined
                }
                type="email"
                autoComplete="off"
                placeholder="Add people by email"
                value={draft}
                disabled={!state}
                onChange={(e) => {
                  setField(e.target.value);
                  setActive(0);
                  setDismissed(false);
                  if (note) setNote(null);
                }}
                onKeyDown={onKeyDown}
                onPaste={onPaste}
                onBlur={() => setDismissed(true)}
                className="h-8 min-w-30 flex-1 bg-transparent text-3.5 text-fg-default outline-none placeholder:text-fg-muted"
              />
              <span className="ml-auto whitespace-nowrap px-2 text-3 text-fg-muted">
                Can view
              </span>
            </div>
            {shown.length > 0 && (
              <div
                id={listId}
                role="listbox"
                aria-label="People you have shared with"
                className="absolute inset-x-0 top-13 z-10 rounded-[14px] bg-bg-elevated p-1.5 shadow-lg"
              >
                {shown.map((p, i) => (
                  <button
                    key={p.email}
                    id={`${listId}-${i}`}
                    type="button"
                    role="option"
                    tabIndex={-1}
                    aria-selected={i === active}
                    // Keeps focus in the field, so its blur never closes the list mid-pick.
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => void addOne(p.email)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-10 px-2.5 py-2 text-left",
                      i === active && "bg-bg-muted"
                    )}
                  >
                    <Avatar name={p.name} seed={p.email} size={28} />
                    <span className="min-w-0">
                      <span className="block truncate text-3.5 font-medium">
                        {p.name}
                      </span>
                      <span className="block truncate text-3 text-fg-muted">
                        {p.email}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Always mounted, so what the field says is heard when it changes. */}
          <div role="status" aria-live="polite">
            {note && (
              <NoteLine
                note={note}
                onInvite={(link) => void copy(link, "Invite link copied.")}
              />
            )}
          </div>

          <div className="mt-5 text-3 font-semibold">People with access</div>
          {state ? (
            <ul className="mt-2 grid gap-0.5">
              <PersonRow
                name={`${state.owner.name} (you)`}
                email={state.owner.email}
                role="Owner"
                seed={state.owner.email}
                owner
              />
              {state.people.map((p) => (
                <PersonRow
                  key={p.userId}
                  name={p.name}
                  email={p.email}
                  role="Can view"
                  seed={p.email}
                  onRemove={() => void remove(p)}
                />
              ))}
            </ul>
          ) : share.isError ? (
            <div className="mt-2 flex items-center gap-3 text-3 text-fg-muted">
              Couldn&apos;t load who has access.
              <button
                type="button"
                onClick={() => void share.refetch()}
                className="font-medium text-fg-default underline underline-offset-3"
              >
                Try again
              </button>
            </div>
          ) : (
            <div aria-busy className="mt-2 grid gap-2">
              <div className="h-12.5 animate-pulse rounded-12 bg-bg-muted" />
            </div>
          )}

          <div className="mt-5 text-3 font-semibold">General access</div>
          <div className="mt-2 grid grid-cols-[36px_minmax(0,1fr)] items-center gap-3 px-1 py-2.5">
            <span
              className={cn(
                "grid size-9 place-items-center rounded-full",
                general === "link"
                  ? "bg-[color-mix(in_srgb,var(--kind-green)_14%,var(--bg-elevated))] text-[color-mix(in_srgb,var(--kind-green)_72%,var(--fg-default))]"
                  : "bg-bg-muted text-fg-default"
              )}
            >
              {general === "link" ? (
                <GlobeIcon className="size-4.5" />
              ) : (
                <LockIcon className="size-4.5" />
              )}
            </span>
            <div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild disabled={!state}>
                  <button
                    type="button"
                    className="-ml-2 inline-flex items-center gap-1.5 rounded-8 px-2 py-1 text-3.5 font-medium hover:bg-bg-muted"
                  >
                    {g.label}
                    <ChevronIcon className="size-3.5 text-fg-muted" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="min-w-64 rounded-[14px] p-1.5"
                >
                  {(["restricted", "link"] as const).map((v) => (
                    <DropdownMenuItem
                      key={v}
                      onSelect={() => void changeGeneral(v)}
                      className="grid grid-cols-[20px_1fr] items-start gap-2.5 rounded-10 py-2"
                    >
                      <span aria-hidden className="text-fg-default">
                        {general === v ? "✓" : ""}
                      </span>
                      <span>
                        <span className="block text-3.5 font-medium text-fg-default">
                          {GENERAL[v].label}
                        </span>
                        <span className="block text-3 font-normal text-fg-muted">
                          {GENERAL[v].menu}
                        </span>
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <div className="mt-px text-3 text-fg-muted">{g.consequence}</div>
            </div>
          </div>

          <div className="mt-4.5 flex items-center gap-2">
            <button
              type="button"
              onClick={() =>
                void copy(shareUrl(state?.id ?? target.id), g.copied)
              }
              disabled={!state}
              className="inline-flex h-9.5 items-center gap-2 rounded-full bg-bg-muted px-4 text-3.5 font-medium hover:bg-border-default"
            >
              <LinkIcon className="size-4" />
              Copy link
            </button>
            <button
              type="button"
              onClick={onClose}
              className="ml-auto inline-flex h-9.5 items-center rounded-full bg-fg-default px-4 text-3.5 font-medium text-bg-base hover:opacity-90"
            >
              Done
            </button>
          </div>
        </div>
        <DialogToast {...toastProps} />
      </DialogContent>
    </Dialog>
  );
}

function PersonRow({
  name,
  email,
  role,
  seed,
  owner = false,
  onRemove,
}: {
  name: string;
  email: string;
  role: string;
  seed: string;
  owner?: boolean;
  onRemove?: () => void;
}) {
  return (
    <li className="group grid grid-cols-[36px_minmax(0,1fr)_auto_32px] items-center gap-3 rounded-12 px-1 py-2 hover:bg-bg-muted">
      <Avatar name={name} seed={seed} owner={owner} />
      <span className="min-w-0">
        <span className="block truncate text-3.5 font-medium">{name}</span>
        <span className="block truncate text-3 text-fg-muted">{email}</span>
      </span>
      <span className="text-3 text-fg-muted">{role}</span>
      {onRemove ? (
        <button
          type="button"
          aria-label={`Remove ${name}`}
          onClick={onRemove}
          className="size-7.5 rounded-full text-4.5 text-fg-muted hover:bg-border-default hover:text-fg-default can-hover:opacity-0 can-hover:group-hover:opacity-100 can-hover:focus-visible:opacity-100"
        >
          ×
        </button>
      ) : (
        <span />
      )}
    </li>
  );
}

function NoteLine({
  note,
  onInvite,
}: {
  note: Note;
  onInvite: (link: string) => void;
}) {
  // Mixed toward the ink, not black, so it holds AA on the dark theme's surface too.
  const warn =
    "text-[color-mix(in_srgb,var(--kind-amber)_55%,var(--fg-default))]";
  if (note.tone !== "no_account") {
    return (
      <div
        className={cn(
          "mx-0.5 mt-2 text-3",
          note.tone === "warn" ? warn : "text-fg-muted"
        )}
      >
        {note.text}
      </div>
    );
  }
  return (
    <div
      className={cn(
        "mx-0.5 mt-2 flex flex-wrap items-center gap-2 text-3",
        warn
      )}
    >
      <span>
        No Vestra account uses{" "}
        <b className="font-medium text-fg-default">{note.email}</b>.
        {note.invite ? "" : " Ask them to sign up, then add them here."}
      </span>
      {note.invite && (
        <button
          type="button"
          onClick={() => onInvite(note.invite!)}
          className="font-medium text-fg-default underline underline-offset-3"
        >
          Copy invite link
        </button>
      )}
    </div>
  );
}
